import React, { useEffect, useState } from 'react'
import { Box, Button, TextField } from '@mui/material'
import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import { useIdsFromPastedString, useTreeIdRouting } from './navigationUtilities'
import { allProductsById } from '../../../api/masters.atoms'
import { ProductSelection } from '../products/ProductSelection'
import { useGetTreeById } from '../../../reducers/trees_hooks'
import { createIdMap } from '../../version_mapping/utility'

export function TreeNodeRouter() {
  const [fromMasters, setFromMasters] = useState(false)
  const routingFunc = useTreeIdRouting()
  return (
    <Box>
      <TreeIdInput onChange={routingFunc} />
      <Button onClick={() => setFromMasters(true)}>Editable Trees</Button>
      {fromMasters && <EditableTreeSelect onSelect={routingFunc} />}
    </Box>
  )
}

export function EditableTreeSelect({
  onSelect,
}: {
  onSelect: (ids: { treeId: string; version: string }) => void
}) {
  const allProducts = useAtomValue(allProductsById)

  if (!allProducts || !Object.keys(allProducts).length) {
    return <Box>Loading...</Box>
  }

  const doSelect = ({ id }: { id: number }) => {
    const product = allProducts[id]
    if (!product?.editable_tree) return
    const { tree_id: treeId, version_id: version } = product.editable_tree
    return onSelect({ treeId, version })
  }

  return (
    <Box width="340px">
      <ProductSelection onSelect={doSelect} />
    </Box>
  )
}

export function TreeIdInput({
  onChange,
  cta = 'Go',
  canSelectTree = () => true,
}: {
  onChange: (ids: {
    treeId: string
    version: string
    versionId: string
    nodeId?: string
    title: string
  }) => void
  cta?: string
  canSelectTree?: (ids: { treeId: string; versionId: string }) => boolean
}) {
  const [currentIdValue, setCurrentIdValue] = useState('')
  const [treeIds, setTreeIds] = useState<{
    treeId?: string | undefined
    versionId?: string | undefined
    nodeId?: string
  }>({})
  const getIds = useIdsFromPastedString()
  const { tree, retrieving } = useGetTreeById(treeIds)

  useEffect(() => {
    const { treeId, version, nodeId } = getIds(currentIdValue)
    setTreeIds({ treeId, versionId: version, nodeId })
  }, [currentIdValue, getIds, setTreeIds])

  useEffect(() => {
    if (!tree) return
    if (!treeIds.nodeId) return
    const { nodeId } = treeIds
    const mapping = createIdMap(tree)
    if (nodeId in mapping) {
      return
    }
    setTreeIds((prev) => ({ ...prev, nodeId: undefined }))
  }, [tree, treeIds.nodeId])

  return (
    <Box>
      Paste treeId:versionId:nodeId or URL here to go to that tree node
      <Box padding={'5px'}>
        <TextField
          label="TreeId or URL"
          fullWidth={true}
          value={currentIdValue}
          onChange={(e) => setCurrentIdValue(e.target.value)}
        />
      </Box>
      {retrieving && <Box>Loading...</Box>}
      {tree && (
        <Box>
          {tree.title}
          <Button
            disabled={
              !canSelectTree({ treeId: tree.tree_id, versionId: tree.version })
            }
            onClick={() =>
              onChange({
                treeId: tree.tree_id,
                version: tree.version,
                versionId: tree.version,
                title: tree.title,
                nodeId: treeIds.nodeId,
              })
            }
          >
            {cta}
          </Button>
        </Box>
      )}
    </Box>
  )
}
